import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProjectLayoutService from 'service/ClientTransmoraService/ProjectLayoutService';
import ProjectKeyService from 'service/ClientTransmoraService/ProjectKeyService';
import LoadingSpinner from 'component/LoadingSpinner';
import { ProjectLayoutCreateReq } from 'domain/payload/request/clientTransmora/projectLayout/ProjectLayoutCreateReq';
import { ProjectLayoutUpdateReq } from 'domain/payload/request/clientTransmora/projectLayout/ProjectLayoutUpdateReq';
import ProjectFilterDropdown from 'component/ProjectFilterDropdown';
import CustomDropdown from 'component/CustomDropdown';
import TemplateBuilder from 'components/templateBuilder/TemplateBuilder';
import DashboardTemplateBuilder from 'components/dashboardBuilder/DashboardTemplateBuilder';
import { KeyItem } from 'components/templateBuilder/templateSerializer';

interface ProjectOption {
  id: string;
  name: string;
}

const ProjectLayoutForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();
  const projectLayoutService = ProjectLayoutService();
  const projectKeyService = ProjectKeyService();

  const isNew = id === '0';

  // INPUT STATE
  const [projectId, setProjectId] = useState('');
  const [type, setType] = useState('html');
  const [template, setTemplate] = useState('');
  const [dashboardTemplate, setDashboardTemplate] = useState('');

  // SUPPORTING DATA
  const [projectList, setProjectList] = useState<ProjectOption[]>([]);
  const [projectKeys, setProjectKeys] = useState<KeyItem[]>([]);

  // LOADING
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const fetchProjects = async () => {
    const token = profile?.data.token;
    if (!token) return;
    setIsLoadingProjects(true);
    try {
      const excludeId = !isNew ? id : undefined;
      const res = await projectLayoutService.ProjectLayoutConfig(token, excludeId);
      setProjectList(res.data?.projectList ?? []);
    } catch (err) {
      console.error('Failed to fetch project list:', err);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) return;
    setIsLoadingDetail(true);
    try {
      const res = await projectLayoutService.ProjectLayoutDetail(token, id!);
      const detail = res.data;
      if (detail) {
        setProjectId(detail.projectId ?? '');
        setTemplate(detail.template ?? '');
        setDashboardTemplate(detail.dashboardTemplate ?? '');
      }
    } catch (err) {
      console.error(err);
      alert('Gagal memuat data layout.');
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const fetchProjectKeys = async (pId: string) => {
    const token = profile?.data.token;
    if (!token || !pId) {
      setProjectKeys([]);
      return;
    }
    setIsLoadingKeys(true);
    try {
      const res = await projectKeyService.ProjectKeyDetail(token, pId);
      const list = res.data?.projectKeyList ?? [];
      setProjectKeys(
        list.map((k: any) => ({
          id: k.id,
          code: k.code,
          name: k.name,
          referenceFactId: k.referenceFactId ?? '',
          keyType: k.keyType || 'text',
          exportAutoSize: k.exportAutoSize,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch project keys:', err);
      setProjectKeys([]);
    } finally {
      setIsLoadingKeys(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!isNew) {
        await fetchDetail();
      }
      await fetchProjects();
    };
    init();
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProjectKeys(projectId);
    } else {
      setProjectKeys([]);
    }
  }, [projectId]);

  const projectLayoutCreate = async () => {
    const token = profile?.data.token;
    if (!token) return;

    const request: ProjectLayoutCreateReq = {
      projectId,
      type,
      template,
      dashboardTemplate,
    };

    console.log(request);
    await projectLayoutService.ProjectLayoutCreate(token, request);
  };

  const projectLayoutUpdate = async () => {
    const token = profile?.data.token;
    if (!token) return;

    const request: ProjectLayoutUpdateReq = {
      id: id!,
      projectId,
      type,
      template,
      dashboardTemplate,
    };

    console.log(request);
    await projectLayoutService.ProjectLayoutUpdate(token, request);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);
    try {
      if (isNew) {
        await projectLayoutCreate();
        alert('Project Layout berhasil dibuat.');
      } else {
        await projectLayoutUpdate();
        alert('Project Layout berhasil disimpan.');
      }
      navigate('/client-project-layout');
    } catch (err) {
      alert(err);
    } finally {
      setIsLoadingSave(false);
    }
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{isNew ? 'Add Project Layout' : 'Edit Project Layout'}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-12">
                  <h6 className="fw-bold mb-3">Basic Information</h6>

                  {/* Project */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Project <span className="text-danger">*</span>
                    </label>
                    <ProjectFilterDropdown
                      projects={projectList as any}
                      defaultSelectedId={projectId || null}
                      onChange={(p) => setProjectId(p ? p.id : '')}
                      placeholder={isLoadingProjects ? 'Memuat project...' : '-- Pilih Project --'}
                      containerClassName="w-100"
                      buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                      disabled={isLoadingProjects}
                    />
                  </div>

                  {/* Type — hardcode "html", disabled */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Type</label>
                    <CustomDropdown
                      value={type}
                      disabled
                      options={[{ id: 'html', name: 'Html' }]}
                      onChange={(opt) => setType(opt.id ? String(opt.id) : '')}
                      placeholder="-- Pilih Type --"
                      containerClassName="w-100"
                      buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                    />
                  </div>

                  {/* Visual Layout Template Builder */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Layout Template Builder</label>
                    <TemplateBuilder
                      value={template}
                      onChange={setTemplate}
                      keys={projectKeys}
                      isLoadingKeys={isLoadingKeys}
                    />
                  </div>

                  {/* Visual Dashboard Template Builder */}
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Dashboard Template Builder</label>
                    <DashboardTemplateBuilder
                      value={dashboardTemplate}
                      onChange={setDashboardTemplate}
                      projectId={projectId}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex justify-content-end">
                <Link to="/client-project-layout">
                  <button type="button" className="btn btn-outline-primary">
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && (
                    <>
                      <LoadingSpinner reverse={true} />
                      <p className="ms-2"></p>
                    </>
                  )}
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectLayoutForm;